// Multi-TCG API Service
// Supports: Pokemon, One Piece, Dragon Ball Fusion, Digimon, Magic, Gundam, Star Wars, Union Arena, Riftbound

const API_KEY = '7a706f1cf742e956fdab403f7d8310c31e69eac6dfc78a492ab5f4761a96f256';
const BASE_URL = 'https://www.apitcg.com/api';
const SCRYFALL_URL = 'https://api.scryfall.com';
const SWUDB_URL = 'https://api.swu-db.com';
const YGOPRODECK_URL = 'https://db.ygoprodeck.com/api/v7';

// Game configuration
export const SUPPORTED_GAMES = [
  {
    id: 'pokemon',
    name: 'Pokemon',
    endpoint: 'pokemon',
    color: '#ffcb05',
    searchFields: ['id', 'name'],
    icon: 'pokeball'
  },
  {
    id: 'yugioh',
    name: 'Yu-Gi-Oh!',
    endpoint: 'yugioh',
    color: '#6b4c9a',
    searchFields: ['id', 'name'],
    icon: 'yugioh'
  },
  {
    id: 'one-piece',
    name: 'One Piece',
    endpoint: 'one-piece',
    color: '#c41e3a',
    searchFields: ['id', 'code', 'rarity', 'type', 'name', 'cost', 'power', 'counter', 'color', 'family', 'ability', 'trigger'],
    icon: 'jolly-roger'
  },
  {
    id: 'dragon-ball',
    name: 'Dragon Ball',
    endpoint: 'dragon-ball-fusion',
    color: '#ff6b00',
    searchFields: ['id', 'name'],
    icon: 'dragon-ball'
  },
  {
    id: 'digimon',
    name: 'Digimon',
    endpoint: 'digimon',
    color: '#1e90ff',
    searchFields: ['id', 'name'],
    icon: 'digimon'
  },
  {
    id: 'magic',
    name: 'Magic: The Gathering',
    endpoint: 'magic',
    color: '#8b4513',
    searchFields: ['id', 'name'],
    icon: 'magic'
  },
  {
    id: 'gundam',
    name: 'Gundam',
    endpoint: 'gundam',
    color: '#e60012',
    searchFields: ['id', 'name'],
    icon: 'gundam'
  },
  {
    id: 'star-wars',
    name: 'Star Wars',
    endpoint: 'star-wars-unlimited',
    color: '#ffe81f',
    searchFields: ['id', 'name'],
    icon: 'star-wars'
  },
  {
    id: 'union-arena',
    name: 'Union Arena',
    endpoint: 'union-arena',
    color: '#9932cc',
    searchFields: ['id', 'name'],
    icon: 'union-arena'
  },
  {
    id: 'riftbound',
    name: 'Riftbound (LoL)',
    endpoint: 'riftbound',
    color: '#0ac8b9',
    searchFields: ['id', 'name'],
    icon: 'riftbound'
  }
];

const headers = {
  'x-api-key': API_KEY,
};

// Search Magic cards using Scryfall API
export const searchMagicCards = async (searchParams = {}) => {
  try {
    let query = searchParams.name ? `name:${searchParams.name}` : '*';
    if (searchParams.rarity) {
      query += ` rarity:${searchParams.rarity.toLowerCase()}`;
    }

    const url = `${SCRYFALL_URL}/cards/search?q=${encodeURIComponent(query)}&page=1`;

    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 404) {
        return []; // No cards found
      }
      throw new Error('Failed to fetch Magic cards');
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching Magic cards:', error);
    return [];
  }
};

// Search Star Wars cards using SWU-DB API
export const searchStarWarsCards = async (searchParams = {}) => {
  try {
    let url = `${SWUDB_URL}/cards/search`;
    const queryParts = [];

    if (searchParams.name) {
      queryParts.push(searchParams.name);
    }
    if (searchParams.rarity) {
      queryParts.push(`r:${searchParams.rarity}`);
    }

    const query = queryParts.length > 0 ? queryParts.join(' ') : '';
    if (query) {
      url += `?q=${encodeURIComponent(query)}`;
    }

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Failed to fetch Star Wars cards');
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching Star Wars cards:', error);
    return [];
  }
};

// Search Yu-Gi-Oh cards using YGOProDeck API
export const searchYuGiOhCards = async (searchParams = {}) => {
  try {
    let url = `${YGOPRODECK_URL}/cardinfo.php`;
    const queryParams = new URLSearchParams();

    if (searchParams.name) {
      queryParams.append('fname', searchParams.name);
    }
    if (searchParams.type) {
      queryParams.append('type', searchParams.type);
    }

    // Limit results
    queryParams.append('num', '100');
    queryParams.append('offset', '0');

    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    } else {
      // Default search if no params
      url += '?num=100&offset=0';
    }

    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 400) {
        return []; // No cards found
      }
      throw new Error('Failed to fetch Yu-Gi-Oh cards');
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching Yu-Gi-Oh cards:', error);
    return [];
  }
};

// Search cards for any game
export const searchGameCards = async (gameEndpoint, searchParams = {}) => {
  try {
    // Use Scryfall for Magic: The Gathering
    if (gameEndpoint === 'magic') {
      return await searchMagicCards(searchParams);
    }

    // Use SWU-DB for Star Wars Unlimited
    if (gameEndpoint === 'star-wars-unlimited') {
      return await searchStarWarsCards(searchParams);
    }

    // Use YGOProDeck for Yu-Gi-Oh
    if (gameEndpoint === 'yugioh') {
      return await searchYuGiOhCards(searchParams);
    }

    let url = `${BASE_URL}/${gameEndpoint}/cards`;

    const queryParams = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value);
      }
    });

    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${gameEndpoint} cards`);
    }

    const data = await response.json();
    return data.data || data.cards || data;
  } catch (error) {
    console.error(`Error fetching ${gameEndpoint} cards:`, error);
    throw error;
  }
};

// Get featured cards for each game (for home page gallery)
export const getFeaturedOnePieceCards = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/one-piece/cards?name=luffy`,
      { headers }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch One Piece cards');
    }

    const data = await response.json();
    // Return first 4 cards or all if less than 4
    return (data.data || data.cards || []).slice(0, 4);
  } catch (error) {
    console.error('Error fetching One Piece cards:', error);
    return [];
  }
};

export const getFeaturedDragonBallCards = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/dragon-ball-fusion/cards?name=goku`,
      { headers }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch Dragon Ball cards');
    }

    const data = await response.json();
    return (data.data || data.cards || []).slice(0, 4);
  } catch (error) {
    console.error('Error fetching Dragon Ball cards:', error);
    return [];
  }
};

export const getFeaturedDigimonCards = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/digimon/cards?name=agumon`,
      { headers }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch Digimon cards');
    }

    const data = await response.json();
    return (data.data || data.cards || []).slice(0, 4);
  } catch (error) {
    console.error('Error fetching Digimon cards:', error);
    return [];
  }
};

export const getFeaturedStarWarsCards = async () => {
  try {
    const response = await fetch(
      `${SWUDB_URL}/cards/search?q=luke`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch Star Wars cards');
    }

    const data = await response.json();
    return (data.data || []).slice(0, 4);
  } catch (error) {
    console.error('Error fetching Star Wars cards:', error);
    return [];
  }
};

export const getFeaturedMagicCards = async () => {
  try {
    const response = await fetch(
      `${SCRYFALL_URL}/cards/search?q=type:dragon+is:popular&order=edhrec`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch Magic cards');
    }

    const data = await response.json();
    return (data.data || []).slice(0, 4);
  } catch (error) {
    console.error('Error fetching Magic cards:', error);
    return [];
  }
};

export const getFeaturedGundamCards = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/gundam/cards?name=gundam`,
      { headers }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch Gundam cards');
    }

    const data = await response.json();
    return (data.data || data.cards || []).slice(0, 4);
  } catch (error) {
    console.error('Error fetching Gundam cards:', error);
    return [];
  }
};

// Get all featured cards for the home page
export const getAllFeaturedCards = async () => {
  try {
    const [onePiece, dragonBall, digimon, starWars] = await Promise.all([
      getFeaturedOnePieceCards(),
      getFeaturedDragonBallCards(),
      getFeaturedDigimonCards(),
      getFeaturedStarWarsCards()
    ]);

    return {
      onePiece,
      dragonBall,
      digimon,
      starWars
    };
  } catch (error) {
    console.error('Error fetching featured cards:', error);
    throw error;
  }
};

// onepiece-cardgame.com serves images with CORP: same-site, which browsers
// block when embedded cross-origin. Route through a public image proxy.
const proxyIfBlocked = (url) => {
  if (!url) return url;
  if (url.includes('en.onepiece-cardgame.com')) {
    return `https://images.weserv.nl/?url=${encodeURIComponent(url.replace(/^https?:\/\//, ''))}`;
  }
  return url;
};

// Helper to get image URL based on game and card data
export const getCardImageUrl = (card, game) => {
  // Star Wars cards use FrontArt
  if (card.FrontArt) return card.FrontArt;

  // Magic cards use image_uris - prefer normal (full card) over art_crop
  if (card.image_uris) {
    return card.image_uris.normal || card.image_uris.large || card.image_uris.small;
  }

  // Yu-Gi-Oh uses card_images array
  if (card.card_images && card.card_images[0]) {
    return card.card_images[0].image_url;
  }

  // Pokemon and others use images object
  if (card.images) {
    if (typeof card.images === 'string') return proxyIfBlocked(card.images);
    return proxyIfBlocked(card.images.large || card.images.small);
  }

  // Generic fallbacks
  if (card.image) return proxyIfBlocked(card.image);
  if (card.imageUrl) return proxyIfBlocked(card.imageUrl);
  if (card.image_url) return proxyIfBlocked(card.image_url);

  return null;
};

// Helper to get card name
export const getCardName = (card) => {
  // Star Wars cards use Name (capital N)
  return card.Name || card.name || card.cardName || card.card_name || 'Unknown Card';
};

// Helper to get rarity
export const getCardRarity = (card) => {
  const rarity = card.Rarity || card.rarity || 'common';
  // Capitalize first letter for display
  return rarity.charAt(0).toUpperCase() + rarity.slice(1);
};
